import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export interface ChecklistItem {
  id: number;
  text: string;
  category?: string;
}

export interface ChecklistProgress {
  id: string;
  user_id: string;
  step_id: number;
  checklist_item_id: number;
  is_completed: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface UseStepChecklistReturn {
  checklistProgress: ChecklistProgress[];
  loading: boolean;
  error: string | null;
  toggleChecklistItem: (stepId: number, itemId: number) => Promise<void>;
  getItemProgress: (stepId: number, itemId: number) => ChecklistProgress | undefined;
  getStepCompletionRate: (stepId: number, totalItems: number) => number;
  refreshChecklist: () => Promise<void>;
}

export function useStepChecklist(): UseStepChecklistReturn {
  const { user } = useAuth();
  const [checklistProgress, setChecklistProgress] = useState<ChecklistProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChecklist = useCallback(async () => {
    if (!user) {
      setChecklistProgress([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('research_step_checklist')
        .select('*')
        .eq('user_id', user.id)
        .order('step_id', { ascending: true });

      if (fetchError) throw fetchError;

      setChecklistProgress(data || []);
    } catch (err) {
      console.error('Error fetching checklist:', err);
      setError(err instanceof Error ? err.message : 'チェックリストの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  const toggleChecklistItem = async (stepId: number, itemId: number) => {
    if (!user) {
      throw new Error('ログインが必要です');
    }

    try {
      const existingItem = checklistProgress.find(
        (p) => p.step_id === stepId && p.checklist_item_id === itemId
      );

      const now = new Date().toISOString();

      if (existingItem) {
        // トグル：完了⇔未完了
        const newIsCompleted = !existingItem.is_completed;
        const { error: updateError } = await supabase
          .from('research_step_checklist')
          .update({
            is_completed: newIsCompleted,
            completed_at: newIsCompleted ? now : null,
            updated_at: now,
          })
          .eq('id', existingItem.id);

        if (updateError) throw updateError;
      } else {
        // 新規作成（最初のチェック）
        const { error: insertError } = await supabase
          .from('research_step_checklist')
          .insert({
            user_id: user.id,
            step_id: stepId,
            checklist_item_id: itemId,
            is_completed: true,
            completed_at: now,
          });

        if (insertError) throw insertError;
      }

      // データを再取得
      await fetchChecklist();
    } catch (err) {
      console.error('Error toggling checklist item:', err);
      throw err;
    }
  };

  const getItemProgress = (stepId: number, itemId: number): ChecklistProgress | undefined => {
    return checklistProgress.find(
      (p) => p.step_id === stepId && p.checklist_item_id === itemId
    );
  };

  const getStepCompletionRate = (stepId: number, totalItems: number): number => {
    if (totalItems === 0) return 0;
    
    const completedItems = checklistProgress.filter(
      (p) => p.step_id === stepId && p.is_completed
    ).length;
    
    return Math.round((completedItems / totalItems) * 100);
  };

  const refreshChecklist = async () => {
    await fetchChecklist();
  };

  return {
    checklistProgress,
    loading,
    error,
    toggleChecklistItem,
    getItemProgress,
    getStepCompletionRate,
    refreshChecklist,
  };
}
